import { getConfig, resolveRoot } from '../shared/utils.js';
import { aiFetch } from './ai-errors.js';
import { ANONYMOUS, NO_SUBSCRIPTION, AUDIO_ONLY, NO_LANG_TOKENS, CONDITIONS_PENDING } from './ai-error-defs.js';

const REQUIRED_CONDITIONS = ['terms'];

const OK_CACHE_KEYS = {
    full: 'ai-preflight-ok',
    basic: 'ai-preflight-ok-no-lang-tokens',
};

const okCache = {
    full: sessionStorage.getItem(OK_CACHE_KEYS.full) === '1',
    basic: sessionStorage.getItem(OK_CACHE_KEYS.basic) === '1',
};

let conditionsAccepted = false;
let langTokensCreated = false;

const tasks = {};
const conditions = {};

const isAnonymous = !getConfig('login');
const hasEligibleSubscription = getConfig('aiLevel') !== 'none';
const isAudioOnly = getConfig('aiLevel') === 'none-audio';

function hasLangTokensRow() {
    return langTokensCreated || !!getConfig('hasLangTokens');
}

async function runOnce(key, fn) {
    if (tasks[key]) return tasks[key];

    tasks[key] = fn();

    try {
        return await tasks[key];
    } finally {
        delete tasks[key];
    }
}

async function fetchAcceptedConditions(headers) {
    const res = await aiFetch(`${getConfig('lang_server_url')}/profile`, {
        method: 'GET',
        headers,
    });

    const body = await res.json();
    return body.conditions || [];
};

async function pendingConditions(headers) {
    const accepted = await fetchAcceptedConditions(headers);
    return REQUIRED_CONDITIONS.filter((c) => !accepted.includes(c));
};

async function runCheck(headers, langTokens = true) {
    if (isAnonymous) {
        return { ok: false, reason: ANONYMOUS };
    }
    if (!hasEligibleSubscription) {
        return { ok: false, reason: NO_SUBSCRIPTION };
    }
    if (isAudioOnly) {
        return { ok: false, reason: AUDIO_ONLY };
    }

    const cacheKey = langTokens ? 'full' : 'basic';

    if (okCache[cacheKey]) return { ok: true };

    if (!conditionsAccepted) {
        const pending = await pendingConditions(headers);
        if (pending.length > 0) {
            return { ok: false, reason: CONDITIONS_PENDING, data: { pending } };
        }

        conditionsAccepted = true;
    }

    if (langTokens) {
        if (!hasLangTokensRow()) {
            return { ok: false, reason: NO_LANG_TOKENS };
        }
    }

    okCache[cacheKey] = true;
    sessionStorage.setItem(OK_CACHE_KEYS[cacheKey], '1');
    return { ok: true };
};

export const preflight = {
    async check(headers, langTokens = true) {
        return runOnce(`check-${langTokens}`, async () => runCheck(headers, langTokens));
    },

    async acceptCondition(name, headers) {
        return runOnce(name, async () => {
            const url = `${getConfig('lang_server_url')}/profile/condition?condition_name=${encodeURIComponent(name)}`;

            await aiFetch(url, { method: 'POST', headers });
        });
    },

    async getConditionText(name, headers) {
        if (conditions[name]) return conditions[name];

        const text = await runOnce(`${name}_text`, async () => {
            const url = `${getConfig('lang_server_url')}/condition/${encodeURIComponent(name)}`;

            const res = await aiFetch(url, { method: 'GET', headers });
            const body = await res.json();
            return body.message;
        });

        conditions[name] = text;
        return text;
    },

    async initLangTokens() {
        return runOnce('init', async () => {
            const res = await aiFetch(resolveRoot('ai-api/start-using-ai'));

            langTokensCreated = true;

            return res.json();
        });
    },
};
